import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse
} from 'axios';
import { API_URL } from '../config/api';

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
});

const getCsrfToken = (): string | null => {
  try {
    const cookies = document.cookie.split(';');
    let csrfToken = null;
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrftoken') {
        csrfToken = decodeURIComponent(value);
        break;
      }
    }
    return csrfToken;
  } catch (error) {
    console.error('Error al obtener CSRF token:', error);
    return null;
  }
};

// Función para configurar el token Bearer
export const setAuthToken = (token: string | null) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    // Guardar el token en localStorage
    localStorage.setItem('authToken', token);
    console.log('Token Bearer configurado:', api.defaults.headers.common['Authorization']);
  } else {
    delete api.defaults.headers.common['Authorization'];
    // Eliminar el token del localStorage
    localStorage.removeItem('authToken');
    console.log('Token Bearer eliminado');
  }
};

// Agregar una función para inicializar el token al cargar la aplicación
export const initializeAuthToken = () => {
  const token = localStorage.getItem('authToken');
  if (token) {
    setAuthToken(token);
  }
};

// Función para realizar el login
export const login = async (email: string, password: string) => {
  try {
    const response = await api.post('/api/login', { email, password });
    
    if (response.data && response.data.token) {
      setAuthToken(response.data.token);
      return response.data;
    } else {
      throw new Error('No se recibió token de acceso');
    }
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

// Función para realizar el logout
export const logout = async () => {
  try {
    // Realizar la petición de logout al backend
    await api.post('/api/logout');
    
    // Limpiar tokens y estado local
    setAuthToken(null);
    localStorage.removeItem('authToken');
    
    // Limpiar todas las cookies relacionadas con la autenticación
    document.cookie = 'access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'refresh_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'csrftoken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    // Eliminar headers de autorización
    delete api.defaults.headers.common['Authorization'];
    
    return true;
  } catch (error) {
    console.error('Error durante el logout:', error);
    // Aún así, limpiamos el estado local en caso de error
    setAuthToken(null);
    localStorage.removeItem('authToken');
    throw error;
  }
};

// Modificar el interceptor de solicitud para usar el token del localStorage
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    config.headers = config.headers || {};

    // Obtener token del localStorage en lugar de las cookies
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // El resto del código del interceptor permanece igual...
    const method = config.method?.toLowerCase();
    if (method && ['post', 'put', 'patch', 'delete'].includes(method)) {
      try {
        const csrfToken = getCsrfToken();
        if (csrfToken) {
          config.headers['X-CSRFToken'] = csrfToken;
        } else {
          // Si no hay token CSRF, intentar obtener uno nuevo
          await api.get('/api/csrf/');
          const newCsrfToken = getCsrfToken();
          if (newCsrfToken) {
            config.headers['X-CSRFToken'] = newCsrfToken;
          }
        }
      } catch (error) {
        console.error('Error al obtener el token CSRF:', error);
      }
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Interceptor de respuesta para manejar errores de autenticación
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (!error.response) {
      console.error('Error de red o servidor no disponible');
    } else {
      switch (error.response.status) {
        case 401:
          // Solo redirigir si no es una solicitud de verificación de autenticación
          if (!error.config?.url?.includes('/api/get_user')) {
            console.error('Sesión expirada - redirigiendo a login');
            setAuthToken(null);
            window.location.href = '/login';
          }
          break;
        case 403:
          console.error('Acceso denegado - verificar token de autorización y CSRF');
          break;
        case 500:
          console.error('Error interno del servidor');
          break;
        default:
          console.error(`Error: ${error.response.status}`);
      }
    }
    return Promise.reject(error);
  }
);

export default api;